import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff, Gift, Users } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_94b15709-33c0-4b3c-a307-474349afd7f8/artifacts/vb9rqttx_ChatGPT%20Image%20Feb%201%2C%202026%20at%2008_39_11%20AM.png";

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    fullName: '',
    password: '',
    confirmPassword: '',
    referralCode: searchParams.get('ref') || ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      await register(formData.email, formData.username, formData.password, formData.fullName, formData.referralCode);
      toast.success('Welcome to AIH Universe! You received 1,000 AIH tokens as a welcome bonus!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-20" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="glass-panel p-8">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="AIH Universe" className="w-16 h-16 mx-auto mb-4 rounded-xl gold-glow" />
            <h1 className="font-unbounded text-2xl font-bold text-white mb-2">Join the Universe</h1>
            <p className="text-white/60 text-sm">Create your account and start earning</p>
          </div>

          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
              <Gift size={20} className="text-black" />
            </div>
            <div>
              <p className="text-[#D4AF37] font-semibold text-sm">Welcome Bonus</p>
              <p className="text-white/60 text-xs">Get 1,000 AIH tokens when you sign up!</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={18} />
                <Input id="email" type="email" placeholder="you@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="input-cosmic pl-10" required data-testid="register-email" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="username" className="text-white/80">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={18} />
                <Input id="username" type="text" placeholder="Choose a username" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className="input-cosmic pl-10" required data-testid="register-username" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-white/80">Full Name (Optional)</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={18} />
                <Input id="fullName" type="text" placeholder="Your full name" value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })} className="input-cosmic pl-10" data-testid="register-fullname" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="referralCode" className="text-white/80">Referral Code (Optional)</Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={18} />
                <Input id="referralCode" type="text" placeholder="Enter referral code" value={formData.referralCode} onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })} className="input-cosmic pl-10 uppercase" data-testid="register-referral" />
              </div>
              <p className="text-xs text-green-400">Your referrer earns 2 AIH tokens!</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={18} />
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Create a password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="input-cosmic pl-10 pr-10" required data-testid="register-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/60">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/80">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={18} />
                <Input id="confirmPassword" type={showPassword ? 'text' : 'password'} placeholder="Confirm your password" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} className="input-cosmic pl-10" required data-testid="register-confirm-password" />
              </div>
            </div>

            <Button type="submit" className="w-full btn-primary mt-6" disabled={isLoading} data-testid="register-submit">
              {isLoading ? <div className="spinner mx-auto" /> : <>Create Account <ArrowRight className="ml-2" size={18} /></>}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white/60 text-sm">Already have an account? <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="goto-login">Sign in</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
